/**
 * LOCATION: dashboard-business/src/pages/Dashboard.js
 * FIXED: "free" → "BASIC" everywhere in plan label display
 * FA icons, fully responsive, AnnouncementBanner integrated
 */

import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import AnnouncementBanner from '../components/AnnouncementBanner';
import { Copy, Check, MessageSquare, Zap, Users, TrendingUp, Code2, Key } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import API from '../services/api';

const loadFA = () => {
  if (!document.getElementById('fa-cdn-dash')) {
    const fa = document.createElement('link');
    fa.id = 'fa-cdn-dash'; fa.rel = 'stylesheet';
    fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
    document.head.appendChild(fa);
  }
};

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return width;
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return { text: 'Good morning',   icon: 'fa-sun',               color: '#f59e0b' };
  if (h >= 12 && h < 17) return { text: 'Good afternoon', icon: 'fa-cloud-sun',          color: '#667eea' };
  if (h >= 17 && h < 21) return { text: 'Good evening',   icon: 'fa-moon',               color: '#8b5cf6' };
  return                          { text: 'Good night',    icon: 'fa-star-and-crescent',  color: '#374151' };
};

// Normalise plan — "free" shows as "basic"
const normPlan = (p) => (!p || p === 'free' ? 'basic' : p);

const PLAN_DISPLAY = {
  basic:    { label: 'BASIC',    color: '#6b7280' },
  pro:      { label: 'PRO',      color: '#667eea' },
  PREMIUM: { label: 'PREMIUM', color: '#8b5cf6' },
};

const StatCard = ({ icon, iconBg, label, value, sub, accent }) => (
  <div style={{ background: '#fff', padding: 20, borderRadius: 16, border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: -18, right: -18, width: 70, height: 70, borderRadius: '50%', background: iconBg, opacity: 0.4 }} />
    <div style={{ width: 40, height: 40, borderRadius: 12, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
      {icon}
    </div>
    <div style={{ fontSize: 34, fontWeight: 900, color: '#111827', lineHeight: 1, marginBottom: 4 }}>{value}</div>
    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 12, color: '#9ca3af' }}>{sub}</div>
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: accent, borderRadius: '0 0 12px 12px' }} />
  </div>
);

const StepCard = ({ num, title, desc, done }) => (
  <div style={{ display: 'flex', gap: 12, padding: '14px 16px', background: done ? '#f0fdf4' : '#f9fafb', borderRadius: 12, border: `1px solid ${done ? '#bbf7d0' : '#e5e7eb'}` }}>
    <div style={{ width: 32, height: 32, borderRadius: 9, background: done ? '#10b981' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {done
        ? <Check size={15} color="#fff" />
        : <span style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af' }}>{num}</span>}
    </div>
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: done ? '#166534' : '#374151', marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 12, color: done ? '#15803d' : '#9ca3af' }}>{desc}</div>
    </div>
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [stats,     setStats]     = useState(null);
  const [copied,    setCopied]    = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const width = useWindowWidth();

  const isMobile  = width < 640;
  const isTablet  = width >= 640 && width < 1024;

  useEffect(() => { loadFA(); loadStats(); }, []);

  const loadStats = async () => {
    const res = await API.getStats();
    if (res.success) setStats(res.stats);
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(user?.apiKey || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const greeting      = getGreeting();
  const messagesUsed  = stats?.messagesUsed || 0;
  const messageLimit  = stats?.messageLimit || 50;
  const msgPct        = Math.min((messagesUsed / messageLimit) * 100, 100);

  // ── FIXED: normalise plan so "free" never shows ──────────
  const planKey     = normPlan(user?.plan);
  const planDisplay = PLAN_DISPLAY[planKey] || PLAN_DISPLAY.basic;
  const planColor   = planDisplay.color;
  const planLabel   = planDisplay.label;

  const statsGridCols = isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(3, 1fr)';
  const twoColGrid    = isMobile ? '1fr' : '1fr 1fr';
  const fourColGrid   = isMobile ? '1fr 1fr' : isTablet ? '1fr 1fr' : 'repeat(4, 1fr)';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <Sidebar />
        <main style={{ flex: 1, padding: isMobile ? '70px 16px 20px' : '32px', overflowY: 'auto', minWidth: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 22, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <i className={`fa-solid ${greeting.icon}`} style={{ fontSize: 14, color: greeting.color }}></i>
              <p style={{ margin: 0, fontSize: 13, color: '#9ca3af', fontWeight: 600 }}>{greeting.text}</p>
            </div>
            <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 28, fontWeight: 900, color: '#111827', letterSpacing: '-0.5px' }}>
              {user?.businessName || 'Dashboard'}
            </h1>
          </div>
          {/* ── FIXED: plan badge shows BASIC not FREE ── */}
          <div style={{ padding: '7px 14px', borderRadius: 20, fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7, letterSpacing: 0.5, background: planColor + '18', color: planColor, border: `1px solid ${planColor}30`, whiteSpace: 'nowrap' }}>
            <i className="fa-solid fa-circle" style={{ fontSize: 6 }}></i>
            {planLabel} PLAN
          </div>
        </div>

        {/* Announcement banner */}
        <AnnouncementBanner plan={planKey} />

        {/* Messages hero */}
        <div style={{ background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)', borderRadius: 18, padding: isMobile ? 20 : '24px 28px', marginBottom: 16, color: '#fff', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ position: 'absolute', bottom: -40, right: 60, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <p style={{ margin: '0 0 6px', fontSize: 12, opacity: 0.75, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
                <i className="fa-solid fa-comment-dots" style={{ marginRight: 6 }}></i>Messages Used
              </p>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: isMobile ? 40 : 52, fontWeight: 900, lineHeight: 1 }}>{messagesUsed}</span>
                <span style={{ fontSize: 20, opacity: 0.6 }}>/ {messageLimit}</span>
              </div>
            </div>
            <div style={{ width: 48, height: 48, background: 'rgba(255,255,255,0.2)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MessageSquare size={22} color="#fff" />
            </div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 100, height: 8, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ height: '100%', width: `${msgPct}%`, background: '#fff', borderRadius: 100, transition: 'width 1s cubic-bezier(0.4,0,0.2,1)' }} />
          </div>
          <p style={{ margin: 0, fontSize: 12, opacity: 0.7 }}>
            <i className="fa-solid fa-circle-info" style={{ marginRight: 5 }}></i>
            {messageLimit - messagesUsed} messages remaining this billing period
          </p>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: statsGridCols, gap: 14, marginBottom: 24 }}>
          <StatCard icon={<Zap size={18} color="#f59e0b" />}       iconBg="#fef3c7" label="Active Chats" value={stats?.activeChats || 0} sub="Live right now"  accent="#f59e0b" />
          <StatCard icon={<TrendingUp size={18} color="#10b981" />} iconBg="#d1fae5" label="Total Chats"  value={stats?.totalChats  || 0} sub="All time"        accent="#10b981" />
          <StatCard icon={<Users size={18} color="#8b5cf6" />}      iconBg="#ede9fe" label="Team Agents"  value={stats?.totalAgents || 0} sub="Active members"  accent="#8b5cf6" />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginBottom: 20 }}>
          {[{ id: 'overview', label: 'Overview', icon: 'fa-gauge' }, { id: 'install', label: 'Install Widget', icon: 'fa-code' }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ position: 'relative', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: activeTab === tab.id ? '#667eea' : '#9ca3af', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 7, transition: 'color 0.2s' }}>
              <i className={`fa-solid ${tab.icon}`} style={{ fontSize: 13 }}></i>
              {tab.label}
              {activeTab === tab.id && <div style={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg,#667eea,#764ba2)', borderRadius: 2 }} />}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: twoColGrid, gap: 16 }}>

              {/* API Key */}
              <div style={{ background: '#fff', borderRadius: 16, padding: 22, border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: '#eff0ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Key size={17} color="#667eea" />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#111827' }}>Your API Key</h3>
                    <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>Connect the widget to your site</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '11px 14px' }}>
                  <code style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.apiKey || 'Loading...'}
                  </code>
                  <button onClick={copyApiKey} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', background: copied ? '#dcfce7' : '#f3f4f6', color: copied ? '#166534' : '#374151', transition: 'all 0.2s' }}>
                    {copied ? <><Check size={13} />Copied!</> : <><Copy size={13} />Copy</>}
                  </button>
                </div>
              </div>

              {/* Performance */}
              <div style={{ background: '#fff', borderRadius: 16, padding: 22, border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <TrendingUp size={17} color="#f59e0b" />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#111827' }}>Performance</h3>
                    <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>Your usage summary</p>
                  </div>
                </div>
                {[
                  { label: 'Message Usage',    value: `${msgPct.toFixed(0)}%`,              color: msgPct > 80 ? '#ef4444' : '#10b981', icon: 'fa-chart-simple' },
                  { label: 'Active Chats Now', value: stats?.activeChats || 0,              color: '#f59e0b',                            icon: 'fa-bolt' },
                  { label: 'Team Size',        value: `${stats?.totalAgents || 0} agents`,  color: '#8b5cf6',                            icon: 'fa-users' },
                ].map((item, i, arr) => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: i < arr.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 7 }}>
                      <i className={`fa-solid ${item.icon}`} style={{ color: item.color, fontSize: 12 }}></i>
                      {item.label}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: item.color }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Getting Started */}
            <div style={{ background: '#fff', borderRadius: 16, padding: 22, border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fa-solid fa-rocket" style={{ color: '#10b981', fontSize: 17 }}></i>
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#111827' }}>Getting Started</h3>
                  <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>Steps to go live</p>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: twoColGrid, gap: 10 }}>
                <StepCard num="01" title="Account Created"    desc="You're registered and ready"    done={true} />
                <StepCard num="02" title="Copy Your API Key"  desc="Find it in the card above"      done={!!user?.apiKey} />
                <StepCard num="03" title="Install the Widget" desc="Paste one script on your site"  done={false} />
                <StepCard num="04" title="Go Live!"           desc="Start receiving customer chats" done={false} />
              </div>
            </div>
          </div>
        )}

        {/* Install tab */}
        {activeTab === 'install' && (
          <div style={{ background: '#fff', borderRadius: 16, padding: isMobile ? 18 : 24, border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Code2 size={17} color="#374151" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#111827' }}>Quick Install Guide</h3>
                <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>Add this before your closing <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>&lt;/body&gt;</code> tag</p>
              </div>
            </div>

            <div style={{ background: '#0f172a', borderRadius: 14, padding: isMobile ? '18px 16px' : '22px 24px', marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#667eea,#764ba2,#ec4899)' }} />
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                {['#ef4444','#f59e0b','#10b981'].map(c => <div key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />)}
              </div>
              <pre style={{ margin: 0, fontFamily: '"Fira Code","Courier New",monospace', fontSize: isMobile ? 11 : 13, color: '#e2e8f0', lineHeight: 2, overflowX: 'auto', whiteSpace: 'pre' }}>
                <span style={{ color: '#64748b' }}>{'<!-- Janimony Chat Widget -->'}</span>{'\n'}
                <span style={{ color: '#94a3b8' }}>{'<'}</span><span style={{ color: '#7dd3fc' }}>script</span> <span style={{ color: '#86efac' }}>src</span><span style={{ color: '#94a3b8' }}>="</span><span style={{ color: '#fcd34d' }}>https://cdn.janimony.chat/widget.js</span><span style={{ color: '#94a3b8' }}>"&gt;&lt;/script&gt;</span>{'\n'}
                <span style={{ color: '#94a3b8' }}>{'<'}</span><span style={{ color: '#7dd3fc' }}>script</span><span style={{ color: '#94a3b8' }}>&gt;</span>{'\n'}
                {'  '}<span style={{ color: '#c4b5fd' }}>JanimonyChat</span><span style={{ color: '#94a3b8' }}>.</span><span style={{ color: '#86efac' }}>init</span><span style={{ color: '#94a3b8' }}>(&#123;</span>{'\n'}
                {'    '}<span style={{ color: '#fda4af' }}>businessId</span><span style={{ color: '#94a3b8' }}>: '</span><span style={{ color: '#fcd34d' }}>{user?.apiKey || 'YOUR_API_KEY'}</span><span style={{ color: '#94a3b8' }}>'</span>{'\n'}
                {'  '}<span style={{ color: '#94a3b8' }}>&#125;);</span>{'\n'}
                <span style={{ color: '#94a3b8' }}>{'</'}</span><span style={{ color: '#7dd3fc' }}>script</span><span style={{ color: '#94a3b8' }}>&gt;</span>
              </pre>
            </div>

            <button onClick={copyApiKey} style={{ padding: '11px 22px', background: copied ? '#10b981' : 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, transition: 'background 0.3s' }}>
              {copied
                ? <><i className="fa-solid fa-check" style={{ fontSize: 14 }}></i>Copied!</>
                : <><i className="fa-solid fa-copy" style={{ fontSize: 14 }}></i>Copy Code</>}
            </button>

            <div style={{ display: 'grid', gridTemplateColumns: fourColGrid, gap: 12 }}>
              {[
                { num: '1', title: 'Copy the snippet',     desc: 'Click the button above',  icon: 'fa-copy'   },
                { num: '2', title: 'Open your website',    desc: 'Any HTML editor works',   icon: 'fa-globe'  },
                { num: '3', title: 'Paste before </body>', desc: 'Bottom of your HTML',     icon: 'fa-code'   },
                { num: '4', title: "You're live!",         desc: 'Widget appears instantly', icon: 'fa-rocket' },
              ].map(step => (
                <div key={step.num} style={{ padding: '16px 14px', background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb', textAlign: 'center' }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                    <i className={`fa-solid ${step.icon}`} style={{ color: '#fff', fontSize: 14 }}></i>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 3 }}>{step.title}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>{step.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default Dashboard;